import { ChildModel, ModelBase } from 'store/model-base';
import { FraudStatus, OrderItemCategory, OrderStatus, ReturnEligible, ShipmentStatus, SimType } from 'my-account/constants';
import { PaymentInfo } from 'store/credit-card/models';
import { FullAddress } from 'store/user/models';
import { OrderChannel } from './channel';
import { OrderItem } from './item';
import { OrderShipment } from './shipment';
import { OrderTaxFee } from './tax-fee';
import { OrderItemCharge } from './charge';
import { OS } from 'core/constants';
import { CmsExchangeOrder, CmsOrderDetail, CmsReturnOrder } from 'store/cms/models';
import { OrderItemPromotion } from './promotion';
import { FulfillmentDetails } from './fulfillment-details';

export class OrderDetail extends ModelBase {
    public static storeName: string = 'orderDetail';
    public static idAttribute: string = 'id.key';

    public id: Id;
    public status: OrderStatus;
    public fraudStatus: FraudStatus;
    public submittedDate: string;
    public cancelledDate: string;
    public isLegacy: boolean;
    public paymentAuth: PaymentInfo;
    public shippingMethod: ShippingMethod;
    public shippingAddress: FullAddress;
    public shipments: OrderShipment[];
    public orderChannel: OrderChannel;
    public taxesAndFees: OrderTaxFee[];
    public taxTotal: number;
    public promotionalPrepaidCardAmount: number;
    public returnTaxTotal: number;
    public returnEligible: ReturnEligible;
    public isCancelEligible: boolean;
    public orderItems: OrderItem[];
    public totalUnlimitedDataCost: number;
    public totalUnlimitedPhoneDataCost: number;
    public totalUnlimitedTabletDataCost: number;
    public isCharged: boolean;
    public isRefund: boolean;
    public isEsim: boolean;
    public parentOrderId: string;
    public servicePromotionTotal: number;
    public planPriceAfterServicePromo: number;
    public fulfillmentDetails?: FulfillmentDetails[];
    public promoOffBYOBOID: OrderItemPromotion;
    public fulfillmentMethod: string;

    public orderItemsByAccessory: OrderItem[];
    public orderItemsByDevice: OrderItem[];
    public orderItemsByByodDevice: OrderItem[];
    public orderItemsWithPromotions: OrderItem[];
    public byTheGigPlanOrderItems: OrderItem[];
    public byTheGigSharedPlanOrderItems: OrderItem[];
    public unlimitedPlanOrderItems: OrderItem[];
    public tabletPlanOrderItems: OrderItem[];
    public phonesWithUnlimitedPlan: OrderItem[];
    public tabletsWithUnlimitedPlan: OrderItem[];
    public tabletsPlanSix: OrderItem[];
    public tabletPlanSixIntroItems: OrderItem[];
    public tabletPlanSixPremiumItems: OrderItem[];
    public phonesWithUnlIntroPlan: OrderItem[];
    public phonesWithUnlPlusPlan: OrderItem[];
    public phonesWithUnlMobileSelectPlan: OrderItem[];
    public phonesWithUnlPremiumPlan: OrderItem[];
    public tabletsWithUnlIntroPlan: OrderItem[];
    public tabletsWithUnlPlusPlan: OrderItem[];
    public tabletsWithUnlMobileSelectPlan: OrderItem[];
    public tabletsWithUnlPremiumPlan: OrderItem[];
    public smartWatchPlanOrderItems: OrderItem[];
    public unlIntroPlanOrderItems: OrderItem[];
    public unlPlusPlanOrderItems: OrderItem[];
    public unlMobileSelectPlanOrderItems: OrderItem[];
    public unlPremiumPlanOrderItems: OrderItem[];
    public orderItemsWithServiceLinePromo: OrderItem[];
    public orderItemWithServiceLinePromo: OrderItem;
    public orderItemWithServicePromoAccount: OrderItem;
    public servicePromoLine: OrderItemPromotion;
    public planChangeOrderItems: OrderItem[];
    public segmentChangeOrderItems: OrderItem[];
    public promoLinePromotions: OrderItemPromotion[];
    public promotions: OrderItemPromotion[];
    public servicePromoAccount: OrderItemPromotion;
    public promoAccountPromotions: OrderItemPromotion[];
    public isPreOrder: boolean;
    public isBackOrder: boolean;
    public isByod: boolean;
    public hasOnlyByod: boolean;
    public isRegularOrder: boolean;
    public isNdelOrder: boolean;
    public isUpgradeOrder: boolean;
    public isUpgradePreOrder: boolean;
    public isUpgradeBackOrder: boolean;
    public isExchangeOrder: boolean;
    public isNdelPreOrder: boolean;
    public isNdelBackOrder: boolean;
    public isSimLedByod: boolean;
    public canInitiateReturn: boolean;
    public isByodLocked: boolean;
    public hasPrepaidPromo: boolean;
    public hasExtendedPromo: boolean;
    public hasSinglePromoType: boolean;
    public hasExtendedPromoOnly: boolean;
    public hasPrepaidPromoOnly: boolean;
    public hasAnyExtendedPromo: boolean;
    public hasFiveGDevice: boolean;
    public hasOnlyByodEsim: boolean;
    public isInStoreOrder: boolean;
    public isPlanChangeOrder: boolean;
    public isSegmentChangeOrder: boolean;
    public isPsimByodOrder: boolean;

    public ndelOrderItem: OrderItem;

    public static create<T extends ModelBase>(initData: object): T {
        const toReturn: OrderDetail = super.create<OrderDetail>(initData);
        toReturn.orderItemsByAccessory = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.isAccessory);
        toReturn.orderItemsByDevice = toReturn.orderItems.filter((orderItem: OrderItem) => !orderItem.isAccessory);
        toReturn.orderItemsByByodDevice = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.byod);
        toReturn.isByodLocked = toReturn.orderItemsByByodDevice.some((result: OrderItem) => result.isByodDeviceLocked);
        toReturn.isByod = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.byod));
        toReturn.hasOnlyByod = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.byod).length === toReturn.orderItems.length;
        toReturn.isCharged = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isChargedItem));
        toReturn.isRefund = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isRefundItem));
        toReturn.isPreOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isPreOrderItem));
        toReturn.isBackOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isBackOrderItem));
        toReturn.isUpgradeOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isUpgradeItem));
        toReturn.isUpgradePreOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isUpgradePreOrderItem));
        toReturn.isUpgradeBackOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isUpgradeBackOrderItem));
        toReturn.isNdelOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isNdelItem));
        toReturn.isExchangeOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isExchangeItem));
        toReturn.isNdelPreOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isNdelPreOrderItem));
        toReturn.isNdelBackOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isNdelBackOrderItem));
        toReturn.isSimLedByod = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isSimLedByod));
        toReturn.isRegularOrder = !toReturn.orderItems.find((orderItem: OrderItem) => !orderItem.isRegularOrder);
        toReturn.isPlanChangeOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isChangePlan));
        toReturn.isSegmentChangeOrder = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isChangeSegment));
        toReturn.canInitiateReturn = toReturn.returnEligible !== ReturnEligible.INELIGIBLE;
        toReturn.hasExtendedPromo = toReturn.orderItems.some((orderItem: OrderItem) => orderItem.hasExtendedPromo);
        toReturn.hasPrepaidPromo = toReturn.orderItems.some((orderItem: OrderItem) => orderItem.hasPrepaidPromo);
        toReturn.orderItemsWithPromotions = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.promotions.length > 0);
        toReturn.hasSinglePromoType = toReturn.orderItemsWithPromotions.every((orderItem: OrderItem) => orderItem.hasSinglePromo && (orderItem.promotions[0].type === toReturn.orderItemsWithPromotions[0].promotions[0].type));
        toReturn.hasExtendedPromoOnly = toReturn.hasExtendedPromo && toReturn.hasSinglePromoType;
        toReturn.hasPrepaidPromoOnly = toReturn.hasPrepaidPromo && toReturn.hasSinglePromoType;
        toReturn.hasAnyExtendedPromo = toReturn.orderItems.some((orderItem: OrderItem) => orderItem.hasAnyExtendedPromo);
        toReturn.ndelOrderItem = toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isNdelItem || orderItem.isNdelPreOrderItem || orderItem.isNdelBackOrderItem);
        toReturn.byTheGigPlanOrderItems = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.isByTheGig);
        toReturn.byTheGigSharedPlanOrderItems = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.isByTheGigShared);
        toReturn.unlimitedPlanOrderItems = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.isUnlimited);
        toReturn.tabletPlanOrderItems = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.isTabletGroup);
        toReturn.planChangeOrderItems = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.isChangePlan);
        toReturn.segmentChangeOrderItems = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.isChangeSegment);
        toReturn.phonesWithUnlimitedPlan = toReturn.unlimitedPlanOrderItems && toReturn.unlimitedPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.PHONE);
        toReturn.tabletsWithUnlimitedPlan = toReturn.unlimitedPlanOrderItems && toReturn.unlimitedPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.TABLET);
        toReturn.tabletsPlanSix = toReturn.tabletPlanOrderItems && toReturn.tabletPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.TABLET);
        toReturn.tabletPlanSixIntroItems = toReturn.tabletsPlanSix && toReturn.tabletsPlanSix.filter((orderItem: OrderItem) => orderItem.isUnlIntro);
        toReturn.tabletPlanSixPremiumItems = toReturn.tabletsPlanSix && toReturn.tabletsPlanSix.filter((orderItem: OrderItem) => orderItem.isUnlPremium);
        toReturn.smartWatchPlanOrderItems = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.isSmartwatch);
        toReturn.isEsim = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.simType === SimType.ESIM));
        toReturn.unlIntroPlanOrderItems = toReturn.unlimitedPlanOrderItems && toReturn.unlimitedPlanOrderItems.filter((orderItem: OrderItem) => orderItem.isUnlIntro);
        toReturn.unlPlusPlanOrderItems = toReturn.unlimitedPlanOrderItems && toReturn.unlimitedPlanOrderItems.filter((orderItem: OrderItem) => orderItem.isUnlPlus);
        toReturn.unlMobileSelectPlanOrderItems = toReturn.unlimitedPlanOrderItems && toReturn.unlimitedPlanOrderItems.filter((orderItem: OrderItem) => orderItem.isMobileSelect);
        toReturn.unlPremiumPlanOrderItems = toReturn.unlimitedPlanOrderItems && toReturn.unlimitedPlanOrderItems.filter((orderItem: OrderItem) => orderItem.isUnlPremium);
        toReturn.phonesWithUnlIntroPlan = toReturn.unlIntroPlanOrderItems && toReturn.unlIntroPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.PHONE);
        toReturn.phonesWithUnlPlusPlan = toReturn.unlPlusPlanOrderItems && toReturn.unlPlusPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.PHONE);
        toReturn.phonesWithUnlMobileSelectPlan = toReturn.unlMobileSelectPlanOrderItems && toReturn.unlMobileSelectPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.PHONE);
        toReturn.phonesWithUnlPremiumPlan = toReturn.unlPremiumPlanOrderItems && toReturn.unlPremiumPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.PHONE);
        toReturn.tabletsWithUnlIntroPlan = toReturn.unlIntroPlanOrderItems && toReturn.unlIntroPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.TABLET);
        toReturn.tabletsWithUnlPlusPlan = toReturn.unlPlusPlanOrderItems && toReturn.unlPlusPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.TABLET);
        toReturn.tabletsWithUnlMobileSelectPlan = toReturn.unlMobileSelectPlanOrderItems && toReturn.unlMobileSelectPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.TABLET);
        toReturn.tabletsWithUnlPremiumPlan = toReturn.unlPremiumPlanOrderItems && toReturn.unlPremiumPlanOrderItems.filter((orderItem: OrderItem) => orderItem.category === OrderItemCategory.TABLET);
        toReturn.hasOnlyByodEsim = toReturn.hasOnlyByod && toReturn.isEsim; 
        toReturn.isInStoreOrder = toReturn.fulfillmentMethod === 'IN_STORE';
        toReturn.isPsimByodOrder  = Boolean(toReturn.orderItems.find((orderItem: OrderItem) => orderItem.isPsimByodOrder ));

        toReturn.orderItemsWithServiceLinePromo = toReturn.orderItems.filter((orderItem: OrderItem) => orderItem.hasServicePromoLine);
        toReturn.orderItemWithServiceLinePromo = toReturn.orderItems.find((orderItem: OrderItem) => orderItem.hasServicePromoLine);
        toReturn.orderItemWithServicePromoAccount = toReturn.orderItems.find((orderItem: OrderItem) => orderItem.hasServicePromoAccount);
        toReturn.servicePromoAccount = toReturn.promotions.find((promo: OrderItemPromotion) =>  promo.isServiceAccountPromo);
        toReturn.servicePromoLine = toReturn.orderItemWithServiceLinePromo && toReturn.orderItemWithServiceLinePromo.promotions.find((promo: OrderItemPromotion) =>  promo.isServiceLinePromo);
        toReturn.promoOffBYOBOID = toReturn.orderItemWithServiceLinePromo && toReturn.orderItemWithServiceLinePromo.promotions.find((promo: OrderItemPromotion) => promo.isByoPromoOffBOID);
        toReturn.promoLinePromotions = toReturn.orderItemsWithServiceLinePromo.length > 0 && toReturn.orderItemsWithServiceLinePromo.map((orderItem: OrderItem) => orderItem.promotions)
            .flat().filter((promo: OrderItemPromotion) =>  promo.isServiceLinePromo);

        toReturn.promoAccountPromotions = toReturn.promotions.filter((promo: OrderItemPromotion) =>  promo.isServiceAccountPromo);

        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> toReturn;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    protected static get hasOne(): ChildModel[] {
        return [{
            attrName: 'paymentAuth',
            model: PaymentInfo
        }, {
            attrName: 'shippingAddress',
            model: FullAddress
        }, {
            attrName: 'orderChannel',
            model: OrderChannel
        }, {
            attrName: 'ndelOrderItem',
            model: OrderItem
        }];
    }

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'orderItems',
            model: OrderItem
        }, {
            attrName: 'shipments',
            model: OrderShipment
        }, {
            attrName: 'taxesAndFees',
            model: OrderTaxFee
        }, {
            attrName: 'orderItemsByAccessory',
            model: OrderItem
        }, {
            attrName: 'orderItemsByDevice',
            model: OrderItem
        }, {
            attrName: 'promotions',
            model: OrderItemPromotion
        }, {
            attrName: 'fulfillmentDetails',
            model: FulfillmentDetails
        }];
    }

    public get carrierInOrder(): string {
        const hasMultipleCarrier: boolean = Boolean((this.orderItemsByByodDevice.find((result: OrderItem, _index: number, array: OrderItem[]) => result.byodCarrier !== array[0].byodCarrier)));

        return hasMultipleCarrier ? undefined : this.orderItemsByByodDevice[0].byodCarrier;
    }

    public get isPlaced(): boolean {
        return this.status === OrderStatus.SUBMITTED && !this.isShipped && !this.isDelivered;
    }

    public get isCanceled(): boolean {
        return this.status === OrderStatus.CANCELLED;
    }

    public get hasShipments(): boolean {
        return Boolean(this.shipments && this.shipments.length && this.shipments[0].shipmentState);
    }

    public get isCompleted(): boolean {
        return this.status === OrderStatus.COMPLETED;
    }

    public get isShipped(): boolean {
        return this.shipments[0] && this.shipments[0].shipmentState === ShipmentStatus.SHIPPED;
    }

    public get isDelivered(): boolean {
        return this.shipments[0] && this.shipments[0].shipmentState === ShipmentStatus.DELIVERED;
    }

    public get isPlanned(): boolean {
        return this.shipments[0] && this.shipments[0].shipmentState === ShipmentStatus.PLANNED;
    }

    public get isRetailScan(): boolean {
        return Boolean(this.orderChannel && this.orderChannel.isRetail && (this.isRegularOrder || this.isExchangeOrder || this.isWarrantyExchangeOrder || this.isNdelOrder || this.isUpgradeOrder)
            && !this.shippingMethod && !this.shippingAddress.hasAddress);
    }

    public get shippingDate(): string {
        const shipment: OrderShipment = this.shipments[0];

        return shipment ? shipment.shippedDate : '';
    }

    public get deliveredDate(): string {
        const shipment: OrderShipment = this.shipments[0];

        return shipment ? shipment.deliveredDate : '';
    }

    public get estDeliveryDate(): string {
        const shipment: OrderShipment = this.shipments[0];

        return shipment ? shipment.estimatedDeliveryDate : '';
    }

    public get estShippingDate(): string {
        // used for backorder
        const shipment: OrderShipment = this.shipments[0];

        return shipment ? shipment.estimatedShipDate : '';
    }

    public get isFulfillmentInfoAvailable(): boolean {
        return Boolean(this.fulfillmentDetails && this.fulfillmentDetails.length > 0);
    }

    public get orderFulfillmentDetails(): FulfillmentDetails {        
        return this.fulfillmentDetails && this.fulfillmentDetails[0];
    }

    public get orderTotal(): number {
        return this.paymentAuth.amount ? this.paymentAuth.amount : 0;
    }

    public get trackingNumber(): string {
        return this.shipments[0] && this.shipments[0].trackingNumber;
    }

    public get orderHasAccessory(): boolean {
        return this.orderItems.some((orderItem: OrderItem) => orderItem.isAccessory);
    }

    public get orderHasDevice(): boolean {
        return this.orderItems.some((orderItem: OrderItem) => !orderItem.isAccessory && !orderItem.byod);
    }

    public get orderHasPhone(): boolean {
        return this.orderItems.some((orderItem: OrderItem) => orderItem.isPhone);
    }

    public get orderHasDeviceWithUnlimitedData(): boolean {
        return this.orderItemsByDevice.some((orderItem: OrderItem) => orderItem.isPlanUnlimited);
    }

    public get orderHasAccessoriesOnly(): boolean {
        return this.orderItems.every((orderItems: OrderItem) => orderItems.isAccessory);
    }

    public get deviceCount(): number {
        return this.orderItemsByDevice.length;
    }

    public get accessoryCount(): number {
        return this.orderItemsByAccessory.length;
    }

    public get deviceDisplayName(): string {
        if (this.deviceCount === 1) {
            return this.orderItemsByDevice[0].productName;
        }
    }

    public get accessoryDisplayName(): string {
        if (this.accessoryCount === 1) {
            return this.orderItemsByAccessory[0].productName;
        }
    }

    public get orderHasAndriodDevice(): boolean {
        return this.orderItemsByDevice.some((orderItem: OrderItem) => orderItem.productDetails.os.toUpperCase() === OS.ANDROID);
    }

    public get orderhasSingleDeviceOs(): boolean {
        const devicesByProductOs: string[] = this.orderItemsByDevice.map((orderItem: OrderItem) => orderItem.productDetails.os);

        return Array.from(new Set(devicesByProductOs)).length === 1;
    }

    public get shippingFullName(): string {
        return `${this.shippingAddress.firstName} ${this.shippingAddress.lastName}`;
    }

    public get isRetailOrder(): boolean {
        return this.orderChannel.isRetail;
    }

    // the same logic is used by routes file to generate breadcrumb
    public getHeading(pageData: CmsOrderDetail): string {
        let orderType: string = '';

        if (this.isCanceled) {
            orderType = pageData.cancelLabel;
        }

        if (this.isPreOrder || this.isNdelPreOrder || this.isUpgradePreOrder) {
            orderType = pageData.preOrder;
        }

        if (this.isUpgradeOrder) {
            orderType = pageData.upgradeLabel;
        }

        return `${pageData.header}${this.id.value} ${orderType}`;
    }

    public getExchangeHeading(pageData: CmsExchangeOrder): string {
        return pageData.header.replace('[ORDER_ID]', this.id.value);
    }

    public getReturnHeading(pageData: CmsReturnOrder): string {
        return pageData.header.replace('[ORDER_ID]', this.id.value);
    }

    public get isWarrantyExchangeOrder(): boolean {
        return this.orderItems.some((item: OrderItem) => item.isWarrantyExchangeItem);
    }

    public get exchangeItem(): OrderItem {
        return this.orderItems.find((item: OrderItem) => item.isExchangeItem || item.isWarrantyExchangeItem);
    }

    public get returnItem(): OrderItem {
        return this.orderItems.find((item: OrderItem) => item.isReturnItem);
    }

    public get iosByodImei(): string {
        const iosByodItem: OrderItem = this.orderItems.find((item: OrderItem) => item.byod && item.isIosDevice);

        return iosByodItem && iosByodItem.deviceImei;
    }

    public get isEsimDeliveredOrder(): boolean {
        return this.orderItems.every((orderItems: OrderItem) => orderItems.isEsim) && this.isDelivered;
    }

    public get isActivationFee(): boolean {
        return this.orderItems.some((item: OrderItem) => item.isActivationFee);
    }

    public get isActivationWaived(): boolean {
        return this.orderItems.some((item: OrderItem) => item.isActivationWaived);
    }

    public getOneTimeActivationContent(oneTimeActivationFeeDescription: string): string {
        const oneTimeActivationOrder: OrderItem = this.orderItems?.find((item: OrderItem) => item?.isActivationFee || item?.isActivationWaived);
        const oneTimeActivationCharge: OrderItemCharge  = oneTimeActivationOrder?.charges?.find((charge: OrderItemCharge) => (charge?.isActivationFee || charge?.isActivationWaived));
        
        return oneTimeActivationFeeDescription?.replace('[PRICE]', oneTimeActivationCharge?.price?.toString());
    }
}
